import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';
import './Toast.css';

/**
 * Baustein — Toast.
 * Globales, transientes Feedback. Einmal per <ToastProvider> in index.js gemountet —
 * an der Wurzel, damit auch AuthPage und die öffentlichen Routen (Impressum,
 * Datenschutz, Kontakt) außerhalb des Auth-Gates Toasts auslösen können.
 * Aufruf in Komponenten: const toast = useToast(); toast.push({ tone, message }).
 *
 * tone: 'success' | 'error' | 'info' (default 'info')
 * action (optional): { label, onClick } — Text-Knopf im Toast (z. B. „Rückgängig");
 * Klick führt onClick aus und schließt den Toast.
 * onMessageClick (optional): macht den Meldungstext selbst zum Knopf (ohne eigene Optik,
 * Fokusring bleibt) — Klick führt onMessageClick aus und schließt den Toast, wie bei action.
 * Viewport unten-mittig, Auto-Dismiss nach 8s, manuell schließbar.
 *
 * Platzierung: eine Seite, deren eigene Bedienelemente unten mittig liegen (Playtest-Brett mit
 * Handfächer), rendert `<ToastPlacement side="right" bottom={140} />`. Solange das Element
 * eingehängt ist, stehen die Toasts am rechten Rand (rechtsbündig, Einflug von rechts), ihre
 * Unterkante `bottom` Pixel über dem Fensterrand; beim Aushängen gilt wieder unten-mittig. Ohne
 * `bottom` bleibt der übliche Abstand. Es gilt das zuletzt eingehängte Element — eine Seite rendert
 * höchstens eines. Gilt auch unterhalb der Mobil-Grenze (dort sonst volle Breite).
 *
 * Für transiente Erfolg-/Fehler-Meldungen gedacht — ein Inline-`EmptyState` oder
 * Feld-Validierung bleibt für persistente Zustände zuständig (die auch nach 8s
 * noch gelten sollen).
 */
const ToastCtx = createContext(null);

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast muss innerhalb <ToastProvider> verwendet werden.');
  return ctx;
}

const DEFAULT_PLACEMENT = { side: 'center', bottom: null };

/** Setzt die Platzierung der Toasts, solange es eingehängt ist (siehe Kopf der Datei). */
export function ToastPlacement({ side = 'center', bottom = null }) {
  const { setPlacement } = useToast();
  useEffect(() => {
    setPlacement({ side, bottom });
    return () => setPlacement(DEFAULT_PLACEMENT);
  }, [setPlacement, side, bottom]);
  return null;
}

const ICONS = { success: CheckCircle2, error: AlertCircle, info: Info };

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [placement, setPlacement] = useState(DEFAULT_PLACEMENT);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(({ tone = 'info', message, action, onMessageClick }) => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, tone, message, action, onMessageClick }]);
    setTimeout(() => dismiss(id), 8000);
    return id;
  }, [dismiss]);

  // Referenz-stabil, damit Konsumenten `toast` in Effekt-Abhängigkeiten führen können: eine
  // neue Objekt-Identität je Provider-Render würde einen Effekt, der selbst einen Toast
  // auslöst, endlos wiederholen.
  const ctxValue = useMemo(() => ({ push, dismiss, setPlacement }), [push, dismiss]);

  return (
    <ToastCtx.Provider value={ctxValue}>
      {children}
      <div
        className={`toast-viewport${placement.side === 'right' ? ' toast-viewport--right' : ''}`}
        style={placement.bottom != null ? { bottom: placement.bottom } : undefined}
        data-testid="toast-viewport"
      >
        {toasts.map((t) => {
          const Icon = ICONS[t.tone] || Info;
          const assertive = t.tone === 'error';
          return (
            <div key={t.id} className={`toast toast--${t.tone}`} role={assertive ? 'alert' : 'status'} aria-live={assertive ? 'assertive' : 'polite'}>
              <Icon size={16} className="toast__icon" aria-hidden />
              {t.onMessageClick ? (
                <button
                  type="button"
                  className="toast__message toast__message--action"
                  onClick={() => { t.onMessageClick(); dismiss(t.id); }}
                >
                  {t.message}
                </button>
              ) : (
                <span className="toast__message">{t.message}</span>
              )}
              {t.action && (
                <button
                  type="button"
                  className="toast__action"
                  onClick={() => { t.action.onClick(); dismiss(t.id); }}
                >
                  {t.action.label}
                </button>
              )}
              <button type="button" className="toast__close" aria-label="Schließen" onClick={() => dismiss(t.id)}>
                <X size={14} aria-hidden />
              </button>
            </div>
          );
        })}
      </div>
    </ToastCtx.Provider>
  );
}
